import type { SEOArticle } from './seo-content';
import { comparisonTable } from '../lib/comparisonTable';

/**
 * Long-form explainers for the glossary terms people actually search for.
 *
 * Search Console shows these five templated lexicon entries earning the most
 * glossary impressions (CRS, OBT, PNR, virtual cards, GDS). They replace the
 * generated entries of the same slug via src/data/knowledge.ts, so
 * seo-content.ts can still be regenerated by scripts/generate-lexicon.mjs.
 *
 * Real industry terms, neutral, no Voyara capability claims.
 */

// A first-party or official page that backs facts in an explainer. Each one
// was opened and checked against the text that cites it. Rendered as the
// article's visible "Sources" list and as `citation` in its JSON-LD.
export interface ArticleSource {
  title: string;
  publisher: string;
  url: string;
}

// Every explainer has to show where its facts come from.
export interface GlossaryArticle extends SEOArticle {
  sources: ArticleSource[];
}

const CATEGORY = 'Corporate & AI Travel Glossary';

// Sources cited by more than one explainer.
const IBM_SABRE: ArticleSource = {
  title: 'Sabre: the first online reservation system',
  publisher: 'IBM',
  url: 'https://www.ibm.com/history/sabre',
};

const AMADEUS_PNR: ArticleSource = {
  title: 'Passenger Name Record: PNR',
  publisher: 'Amadeus',
  url: 'https://amadeus.com/en/travel-glossary/passenger-name-record',
};

const IATA_NDC_FACT_SHEET: ArticleSource = {
  title: 'Distribution with Offers and Orders (NDC) fact sheet',
  publisher: 'IATA',
  url: 'https://www.iata.org/en/iata-repository/pressroom/fact-sheets/fact-sheet-ndc/',
};

const CONCUR_NDC: ArticleSource = {
  title: 'NDC and Concur Travel',
  publisher: 'SAP Concur',
  url: 'https://www.concur.com/blog/article/ndc-corporate-space-and-concur-travel',
};

export const GLOSSARY_ARTICLES: GlossaryArticle[] = [
  {
    slug: 'computer-reservation-system-crs',
    title: 'Computer Reservation System (CRS), Explained',
    description:
      'What a computer reservation system is, how airline and hotel CRS platforms work, how a CRS differs from a GDS and PSS, and why it still matters.',
    category: CATEGORY,
    directAnswer:
      'A computer reservation system (CRS) is the software that holds a travel supplier’s bookable inventory — seats, rooms or cars — and records the reservations made against it. For an airline, the CRS controls which fares are open on each flight and stores every booking as a passenger name record. Global distribution systems grew out of airline CRS platforms that carriers opened to travel agencies.',
    content: [
      'A computer reservation system, also written as computerised reservation system, is the system of record for what a travel supplier can sell. It answers three questions in real time: what inventory exists, what price and conditions apply to it, and who has already booked it. Airlines, hotel groups and car rental companies all run one.',
      '<h2>Where did the CRS come from?</h2>',
      'Before computerised systems, reservation agents recorded each booking on a handwritten card, and processing a single reservation took around 90 minutes. American Airlines and IBM built SABRE to replace that process, and by the mid-1960s it was handling reservations in seconds. Other airlines soon adopted similar systems, often built with IBM.',
      'From the mid-1970s, airlines began installing their reservation terminals in travel agencies. That turned internal inventory systems into sales channels, and those agency-facing networks later separated from the airlines to become the global distribution systems (GDS) in use today.',
      '<h2>What does an airline CRS do?</h2>',
      '<ul><li><strong>Inventory control.</strong> Each flight is divided into booking classes, each with its own fare and seat limit. The CRS opens and closes classes as seats sell, which is why the same seat can cost different amounts on different days.</li><li><strong>Availability and pricing.</strong> It returns which classes are open on a flight and, with the fares the airline has filed, what each one costs.</li><li><strong>Reservations.</strong> Every booking is stored as a passenger name record (PNR) with its own six-character record locator.</li><li><strong>Ticketing link.</strong> The reservation is connected to the electronic ticket that authorises travel.</li></ul>',
      '<h2>How do CRS, GDS and PSS differ?</h2>',
      'The three terms are often used interchangeably, but they describe different layers. A CRS holds one supplier’s inventory. A global distribution system connects many suppliers’ inventory to travel agencies and booking tools, so an agent can search several airlines, hotels and car companies in one place. A passenger service system (PSS) is the wider suite an airline runs, which usually includes the reservation system alongside inventory management and the departure control system used at check-in and boarding.',
      'Many airlines today use a PSS hosted by a technology provider rather than one they built themselves, and “CRS” often refers to the reservation component of that suite.',
      comparisonTable({
        id: 'crs-gds-pss',
        caption: 'CRS, GDS and PSS compared',
        rowHeader: 'Compared on',
        columns: ['Computer reservation system (CRS)', 'Global distribution system (GDS)', 'Passenger service system (PSS)'],
        rows: [
          {
            label: 'What it is',
            cells: [
              'A supplier’s own system of record for inventory and bookings',
              'A network that connects many suppliers’ inventory to travel sellers',
              'An airline’s full suite of passenger systems',
            ],
          },
          {
            label: 'Whose inventory',
            cells: [
              'One airline, hotel group or car rental company',
              'Many airlines, hotels, car rental companies and rail operators',
              'One airline',
            ],
          },
          {
            label: 'Main users',
            cells: [
              'The supplier and the channels it sells through',
              'Travel agencies, travel management companies and booking tools',
              'The airline itself, from reservations to departure control',
            ],
          },
          {
            label: 'What it covers',
            cells: [
              'Availability, pricing and reservations',
              'Search and booking across suppliers, under one booking record',
              'Reservations, inventory, ticketing and departure control',
            ],
          },
          {
            label: 'Examples',
            cells: [
              'American Airlines’ original SABRE; SynXis for hotel groups',
              'Amadeus, Sabre and Travelport',
              'Amadeus Altéa',
            ],
          },
        ],
      }),
      '<h2>Hotel central reservation systems</h2>',
      'In hospitality, CRS usually stands for central reservation system. A hotel group uses it to manage rates and availability across all its properties and to send that inventory to its own website, online travel agencies and the GDS. It is separate from the property management system (PMS), which runs operations inside a single hotel: check-in, room assignment and billing.',
      '<h2>Why does the CRS matter for corporate travel?</h2>',
      'Corporate travel programmes rarely touch a supplier’s CRS directly, but almost everything they rely on depends on it. Negotiated airline and hotel rates are loaded into the supplier’s system, and whether a traveller sees them in a booking tool depends on how that content is distributed. When a flight is changed or cancelled, the change starts in the airline’s reservation system and flows outward to the agency record, which is why an itinerary can briefly disagree with the airline’s own app.',
      'Regulation shaped these systems too. When airline-owned systems dominated agency sales, regulators in the United States and Europe introduced rules in the 1980s against display bias — ranking the owning carrier’s flights above better options. Those rules are one reason neutral, comparable displays became the expected standard. In both sets of rules, “computer reservation system” means these agency-facing, multi-airline systems, the ones the industry now calls GDSs, which is one reason the two terms overlap.',
      'Related terms are defined in the <a href="/knowledge">travel technology lexicon</a>.',
    ],
    faqs: [
      {
        question: 'What is the difference between a CRS and a GDS?',
        answer:
          'A CRS holds one supplier’s inventory and bookings. A GDS connects the inventory of many airlines, hotels and car rental companies to travel agencies and booking tools, so they can be searched and booked in one place.',
      },
      {
        question: 'Is a computer reservation system the same as a computerised reservation system?',
        answer:
          'Yes. Both names describe the same thing. “Computerised” was common in early usage and still appears in older textbooks and regulation.',
      },
      {
        question: 'Why is a computer reservation system important?',
        answer:
          'It is the single source of truth for what a supplier can sell. It controls availability and fare classes, tracks how many seats or rooms have been sold within any overbooking limits the supplier sets, and stores the booking records that ticketing, check-in and servicing all depend on.',
      },
      {
        question: 'What does CRS mean in hotels?',
        answer:
          'In hospitality, CRS usually means central reservation system: the platform a hotel group uses to manage rates and availability across its properties and distribute them to booking channels.',
      },
    ],
    keywords: [
      'computer reservation system',
      'computerized reservation system',
      'computer reservations system',
      'importance of computer reservation system',
      'crs vs gds',
      'airline reservation system',
    ],
    nlpTerms: [
      'passenger service system',
      'booking class',
      'inventory control',
      'global distribution system',
      'central reservation system',
      'passenger name record',
    ],
    sources: [
      IBM_SABRE,
      AMADEUS_PNR,
      {
        title: 'Altéa Passenger Service System',
        publisher: 'Amadeus',
        url: 'https://amadeus.com/en/airlines/products/altea-passenger-service-system',
      },
      {
        title: 'SynXis Central Reservation System',
        publisher: 'Aven Hospitality',
        url: 'https://www.avenhospitality.com/solutions/crs',
      },
      {
        title: 'OPERA Cloud Property Management data sheet',
        publisher: 'Oracle Hospitality',
        url: 'https://www.oracle.com/a/ocom/docs/industries/hospitality/hosp-opera-cloud-property-manage-ds.pdf',
      },
      {
        title: 'Computer Reservations System (CRS) Regulations, final rule of 7 January 2004',
        publisher: 'U.S. Department of Transportation, Federal Register',
        url: 'https://www.federalregister.gov/documents/2004/01/07/03-32338/computer-reservations-system-crs-regulations',
      },
      {
        title: 'Regulation (EC) No 80/2009 on a Code of Conduct for computerised reservation systems',
        publisher: 'European Union, EUR-Lex',
        url: 'https://eur-lex.europa.eu/eli/reg/2009/80/oj/eng',
      },
    ],
  },
  {
    slug: 'global-distribution-system-gds',
    title: 'Global Distribution System (GDS), Explained',
    description:
      'How a global distribution system connects airlines, hotels and car rental firms to travel agencies, who the major GDS providers are, and what NDC changes.',
    category: CATEGORY,
    directAnswer:
      'A global distribution system (GDS) is a network that aggregates bookable inventory from many travel suppliers — airlines, hotels, car rental companies and rail operators — and makes it searchable and bookable by travel agencies, travel management companies and online booking tools. The three major GDS providers are Amadeus, Sabre and Travelport.',
    content: [
      'In travel, “global distribution” has a specific meaning. A global distribution system sits between the companies that sell travel and the agencies and tools that book it. Instead of connecting separately to hundreds of airlines and hotel groups, an agency connects once to a GDS and searches all of them through it.',
      '<h2>How does a GDS work?</h2>',
      'Airlines publish schedules and file fares through industry channels such as ATPCO, and the GDS combines that data with live availability from each supplier’s reservation system. When an agent or booking tool runs a search, the GDS returns options from many suppliers in a consistent format. When a booking is made, the GDS creates a passenger name record (PNR) and sends the reservation to each supplier involved, which records it in its own system.',
      'A single GDS record can therefore hold a flight from one airline, a hotel from another company and a car rental, all under one record locator, while each supplier keeps its own reference for its part of the trip.',
      '<h2>Who are the major GDS providers?</h2>',
      '<ul><li><strong>Amadeus</strong>, founded in 1987 by Air France, Iberia, Lufthansa and SAS.</li><li><strong>Sabre</strong>, which grew out of the reservation system American Airlines developed with IBM.</li><li><strong>Travelport</strong>, which launched Travelport+ in 2021, the platform previously known as Galileo, and also runs the Apollo and Worldspan systems.</li></ul>',
      '<h2>How is GDS distribution paid for?</h2>',
      'The traditional GDS model charges suppliers a transaction fee for each booking made through it, and the GDS in turn pays travel agencies incentives tied to their booking volume, counted in transactions such as flight segments. This is why distribution cost has become a point of tension: airlines pay for bookings that come through the GDS, while agencies have a reason to keep using it.',
      '<h2>What does NDC change for the GDS?</h2>',
      'GDS airline content has historically been exchanged using EDIFACT, an older messaging standard, with the GDS building offers from the fares, schedules and availability that airlines file. New Distribution Capability (NDC) is an XML standard launched by IATA that lets airlines build their own offers and send richer content — ancillaries such as seats and bags, promotional fares, photos and personalised offers — to sellers, either directly or through intermediaries, including GDS providers.',
      'NDC does not necessarily remove the GDS: the major providers carry NDC content alongside their classic EDIFACT content. What changes is that some airlines now remove certain fares from the GDS or make some content available only through NDC. A booking tool that reads only classic GDS content can miss prices that exist in the market, while one that reads NDC content has to allow for the fact that NDC capabilities, including exchanges and refunds, differ from airline to airline.',
      comparisonTable({
        id: 'gds-ndc',
        caption: 'Classic GDS content and NDC content compared',
        rowHeader: 'Compared on',
        columns: ['Classic GDS content', 'NDC content'],
        rows: [
          { label: 'Message standard', cells: ['EDIFACT', 'XML, to the NDC standard launched by IATA'] },
          {
            label: 'Who builds the offer',
            cells: ['The GDS, from the fares, schedules and availability airlines file', 'The airline'],
          },
          {
            label: 'What an offer can include',
            cells: [
              'Filed fares and fare rules, with any brands and ancillaries the airline has filed',
              'The airline’s full range, including ancillaries, promotional fares, photos and personalised offers',
            ],
          },
          {
            label: 'How it reaches sellers',
            cells: ['Through a GDS', 'Directly from the airline, or through intermediaries including GDSs'],
          },
          {
            label: 'Exchanges and refunds',
            cells: ['Standardised, using the fare rules airlines file', 'Capabilities differ from airline to airline'],
          },
        ],
      }),
      '<h2>Why does the GDS matter for corporate travel programmes?</h2>',
      'For most organisations the GDS is invisible — it sits behind the travel management company and the booking tool. It becomes visible when prices differ between channels, when negotiated fares fail to display, or when changes made directly with an airline do not reach the agency record. Asking a travel management company which content sources it connects to, and how it combines GDS and NDC content, is a practical way to understand what travellers are actually seeing.',
      'Related terms, including CRS, NDC and PNR, are defined in the <a href="/knowledge">travel technology lexicon</a>.',
    ],
    faqs: [
      {
        question: 'What is a GDS in travel?',
        answer:
          'A GDS is a network that aggregates inventory from airlines, hotels and car rental companies so travel agencies and booking tools can search and book it in one place. Amadeus, Sabre and Travelport are the main providers.',
      },
      {
        question: 'What is the difference between a GDS and a CRS?',
        answer:
          'A CRS is one supplier’s own reservation and inventory system. A GDS connects many suppliers’ systems to the agencies and tools that sell travel.',
      },
      {
        question: 'Is NDC replacing the GDS?',
        answer:
          'Not directly. NDC is a messaging standard, and the major GDS providers also distribute NDC content. It changes how airline offers are built and sent, and some airlines now make certain fares or content available only through NDC.',
      },
      {
        question: 'Who pays for a GDS booking?',
        answer:
          'Traditionally the supplier pays the GDS a fee for each booking, and the GDS pays travel agencies incentives tied to how many bookings they make through it.',
      },
    ],
    keywords: [
      'global distribution system',
      'gds travel',
      'what is gds',
      'global distribution definition',
      'gds vs ndc',
      'amadeus sabre travelport',
    ],
    nlpTerms: [
      'New Distribution Capability',
      'EDIFACT',
      'segment fee',
      'travel management company',
      'record locator',
      'content aggregation',
    ],
    sources: [
      {
        title: 'How airline distribution gets offers to flight shoppers',
        publisher: 'ATPCO',
        url: 'https://atpco.net/single-blog/behind-the-scenes-how-airline-distribution-gets-offers-to-flight-shoppers/',
      },
      {
        title: 'What is Amadeus?',
        publisher: 'Amadeus',
        url: 'https://amadeus.com/en/about/overview',
      },
      IBM_SABRE,
      {
        title: 'Travelport+; a next-generation platform for a new era of travel (press release, 7 April 2021)',
        publisher: 'Travelport',
        url: 'https://www.prnewswire.com/news-releases/travelport-a-next-generation-platform-for-a-new-era-of-travel-301263669.html',
      },
      {
        title: 'GDS Formats (Smartpoint help)',
        publisher: 'Travelport',
        url: 'https://support.travelport.com/webhelp/Smartpoint1G1V/Content/Getting%20Started/HostConversions.htm',
      },
      {
        title: 'NDC Guide',
        publisher: 'Travelport',
        url: 'https://developer.travelport.com/docs/flights/ndc/ndc-guide',
      },
      {
        title: 'Annual report on Form 10-K for 2025',
        publisher: 'Sabre Corporation, filed with the U.S. SEC',
        url: 'https://www.sec.gov/Archives/edgar/data/1597033/000162828026008800/sabr-20251231.htm',
      },
      IATA_NDC_FACT_SHEET,
      CONCUR_NDC,
    ],
  },
  {
    slug: 'passenger-name-record-pnr',
    title: 'Passenger Name Record (PNR), Explained',
    description:
      'What a passenger name record is, what a PNR contains, how record locators and ticket numbers differ, and how PNR data reaches governments through PNRGOV.',
    category: CATEGORY,
    directAnswer:
      'A passenger name record (PNR) is the booking record created when a trip is reserved. It holds the traveller’s name, itinerary, contact details and ticketing arrangement, and is identified by a six-character record locator — the booking reference a traveller is given. A PNR is not a ticket: the ticket is a separate record, linked to the PNR, that authorises travel.',
    content: [
      'Every airline booking starts as a passenger name record. When a flight is reserved through an airline website, a travel agency or a corporate booking tool, the reservation system creates a PNR and returns a record locator: the six-character code of letters and numbers shown on a confirmation email.',
      '<h2>What does a PNR contain?</h2>',
      'Airline and agency training commonly teaches the five elements a PNR needs before it can be saved, remembered as PRINT:',
      '<ul><li><strong>Phone</strong> — a contact for the traveller or agency.</li><li><strong>Received from</strong> — who requested the booking.</li><li><strong>Itinerary</strong> — the flight segments or other travel services.</li><li><strong>Name</strong> — each passenger, as it appears on their travel document.</li><li><strong>Ticketing arrangement</strong> — when and how the booking will be ticketed.</li></ul>',
      'Beyond those, a PNR often carries special service requests (for meals, wheelchair assistance or passport details), frequent flyer numbers, fare calculations, agency remarks and, for corporate bookings, data such as cost centre or trip purpose.',
      '<h2>How do the record locator, PNR and ticket number differ?</h2>',
      'These three are often confused. The record locator identifies the PNR. The same trip can have more than one locator: a booking made through a global distribution system has the GDS locator, and each airline on the itinerary creates its own record with its own locator. The electronic ticket is a separate record with a 13-digit number, the first three digits identifying the issuing airline. A reservation that is never ticketed can be cancelled automatically when its ticketing deadline passes.',
      comparisonTable({
        id: 'locator-ticket',
        caption: 'Record locator and e-ticket number compared',
        rowHeader: 'Compared on',
        columns: ['Record locator (booking reference)', 'E-ticket number'],
        rows: [
          { label: 'What it identifies', cells: ['The booking: the PNR', 'One passenger’s ticket'] },
          { label: 'Format', cells: ['Six letters and numbers', '13 digits; the first three identify the airline'] },
          { label: 'When it is created', cells: ['When the booking is made', 'When the ticket is issued, after payment'] },
          { label: 'Who it covers', cells: ['Everyone travelling together on the booking', 'One traveller'] },
          {
            label: 'Where you find it',
            cells: ['On the booking confirmation', 'On the e-ticket and the booking confirmation'],
          },
        ],
      }),
      '<h2>How does PNR data reach governments?</h2>',
      'Because a PNR contains itinerary, contact and payment details, many governments require airlines to transmit PNR data for flights to, from or through their territory. PNRGOV is the standard message format for sending that data, developed by an IATA-led working group of airlines and governments and maintained under a joint committee of the World Customs Organization, IATA and ICAO. In the European Union, Directive (EU) 2016/681 governs how airlines transfer PNR data to national authorities and how it may be used to fight terrorism and serious crime.',
      'PNR data is different from advance passenger information (API). API is travel document data — name, date of birth, passport number — typically collected at check-in. PNR data is booking data, created when the trip is reserved.',
      '<h2>What is a PNR number in Indian Railways?</h2>',
      'In India, PNR is just as familiar from rail travel. Indian Railways issues a 10-digit PNR number for each train booking, which passengers use to check reservation and waitlist status. The idea is the same — a reference to a booking record — though the format differs from an airline record locator.',
      '<h2>Why do PNRs matter in corporate travel?</h2>',
      'The PNR is the unit most corporate travel systems key on. Traveller tracking, duty-of-care alerts, expense matching and reporting all depend on accurate PNR data. When a traveller changes a flight directly with the airline, the airline’s record updates but the agency PNR may not, which is how travel managers end up with reports that disagree with where people actually are.',
      'Related terms such as GDS, NDC and TMC are defined in the <a href="/knowledge">travel technology lexicon</a>.',
    ],
    faqs: [
      {
        question: 'What is a passenger name record?',
        answer:
          'A passenger name record (PNR) is the reservation record for a trip. It holds passenger names, the itinerary, contact details and the ticketing arrangement, and is identified by a record locator.',
      },
      {
        question: 'Is a PNR the same as a ticket number?',
        answer:
          'No. The PNR record locator identifies the booking. The ticket number is a separate 13-digit number identifying the electronic ticket that authorises travel.',
      },
      {
        question: 'What is PNRGOV?',
        answer:
          'PNRGOV is the standard message format airlines use to transmit PNR data to government authorities. It is maintained jointly by the World Customs Organization, IATA and ICAO.',
      },
      {
        question: 'Can one PNR include several passengers?',
        answer:
          'Yes. A PNR can hold several passengers travelling together on the same itinerary, and group bookings can hold many more.',
      },
    ],
    keywords: [
      'passenger name record',
      'what is a passenger name record',
      'pnr passenger name record',
      'pnr gov',
      'pnrgov',
      'record locator',
    ],
    nlpTerms: [
      'record locator',
      'special service request',
      'electronic ticket',
      'advance passenger information',
      'Directive (EU) 2016/681',
      'global distribution system',
    ],
    sources: [
      AMADEUS_PNR,
      {
        title: 'Required Booking File fields (Smartpoint Cloud training)',
        publisher: 'Travelport',
        url: 'https://support.travelport.com/webhelp/SmartpointCloud/Content/Learn/4CreateBF/RequiredFields.htm',
      },
      {
        title: 'Retrieve booking help',
        publisher: 'Virgin Atlantic',
        url: 'https://www.virginatlantic.com/global/en/my-booking/retrieve-booking-help.html',
      },
      {
        title: 'API Guidelines and PNR Reporting Standards',
        publisher: 'World Customs Organization',
        url: 'https://www.wcoomd.org/en/topics/facilitation/instrument-and-tools/tools/api-pnr.aspx',
      },
      {
        title: 'Directive (EU) 2016/681 on the use of passenger name record (PNR) data',
        publisher: 'European Union, EUR-Lex',
        url: 'https://eur-lex.europa.eu/eli/dir/2016/681/oj/eng',
      },
      {
        title: 'Travel Information: passenger name record and advance passenger information',
        publisher: 'European Commission, Migration and Home Affairs',
        url: 'https://home-affairs.ec.europa.eu/policies/internal-security/law-enforcement-cooperation/travel-information_en',
      },
      {
        title: 'PNR Enquiry',
        publisher: 'Indian Railways',
        url: 'https://www.indianrail.gov.in/enquiry/PNR/PnrEnquiry.html?locale=en',
      },
    ],
  },
  {
    slug: 'online-booking-tool-obt',
    title: 'Online Booking Tool (OBT) in Business Travel',
    description:
      'What an online booking tool is in corporate travel, how an OBT applies travel policy, how it works with a TMC, and what to evaluate when choosing one.',
    category: CATEGORY,
    directAnswer:
      'An online booking tool (OBT), also called a self-booking tool, is the software employees use to search and book flights, hotels, rail and cars within their organisation’s travel programme. It shows negotiated rates, applies travel policy at the moment of booking, routes out-of-policy trips for approval, and passes the reservation to a travel management company for ticketing and support.',
    content: [
      'In business travel, OBT stands for online booking tool. It is the self-service front end of a corporate travel programme: where an employee goes to book a trip without calling a travel agent. Self-booking tool (SBT) and corporate booking tool mean the same thing.',
      '<h2>What does an OBT do?</h2>',
      '<ul><li><strong>Search.</strong> It pulls flight, hotel, rail and car options from content sources such as global distribution systems, direct airline NDC connections and hotel aggregators.</li><li><strong>Policy.</strong> It applies the organisation’s travel rules — cabin class, fare limits, advance booking, preferred suppliers — and marks each option as in or out of policy.</li><li><strong>Negotiated rates.</strong> It displays the corporate fares and hotel rates the organisation has agreed with suppliers.</li><li><strong>Approvals.</strong> It routes trips that break policy or exceed a budget to a manager for approval before they are booked.</li><li><strong>Profiles.</strong> It stores traveller details, loyalty numbers and preferences so bookings are accurate.</li></ul>',
      '<h2>How does an OBT work with a travel management company?</h2>',
      'An online booking tool rarely works alone. When a traveller completes a booking, the tool creates a passenger name record and hands it to the travel management company (TMC). The TMC completes ticketing, checks the booking against quality controls, and handles changes, cancellations and out-of-hours support. Bookings made through the tool usually carry a lower TMC transaction fee than bookings made by phone or email, because less agent time is involved.',
      comparisonTable({
        id: 'obt-tmc',
        caption: 'Online booking tool and travel management company compared',
        rowHeader: 'Compared on',
        columns: ['Online booking tool (OBT)', 'Travel management company (TMC)'],
        rows: [
          {
            label: 'What it is',
            cells: [
              'Software for booking travel without the help of a travel agent',
              'A travel agency that arranges, tickets and supports an organisation’s business travel',
            ],
          },
          {
            label: 'Who uses it',
            cells: ['Travellers and the people who book for them', 'Travellers and travel managers who need an agent'],
          },
          {
            label: 'Main jobs',
            cells: [
              'Search, policy checks, negotiated rates, approvals and traveller profiles',
              'Ticketing, quality control, changes and refunds, and out-of-hours support',
            ],
          },
          {
            label: 'The booking record',
            cells: ['Creates it and passes it to the TMC', 'Completes it and issues the ticket'],
          },
          {
            label: 'Transaction fee',
            cells: [
              'Bookings made in the tool are usually charged the lower self-service rate',
              'Bookings made with an agent are charged the higher full-service rate',
            ],
          },
        ],
      }),
      '<h2>How can you tell whether an OBT is working?</h2>',
      '<ul><li><strong>Adoption rate</strong> — the share of eligible bookings made online rather than through an agent.</li><li><strong>Leakage</strong> — trips booked outside the programme entirely, for example directly on a supplier website.</li><li><strong>Policy compliance</strong> — the share of trips booked within policy, and the reasons given when they are not.</li><li><strong>Touchless rate</strong> — bookings ticketed without an agent needing to intervene.</li></ul>',
      'Low adoption usually has practical causes: the tool shows higher prices than travellers find elsewhere, it lacks content they want, or it is slow to use on a phone. Leakage then weakens both duty-of-care visibility and the data used to negotiate supplier rates.',
      '<h2>What should you look for when choosing an OBT?</h2>',
      'Content matters most. Check which airlines are available through NDC as well as the GDS, how hotel content is sourced, and whether the rail operators and low-cost carriers on the organisation’s routes are bookable. Then look at how policy is configured, how approvals follow the organisation’s hierarchy, whether the tool integrates with the chosen TMC and expense system, and how it performs on mobile, where many changes are made.',
      'Related terms such as TMC, PNR and NDC are defined in the <a href="/knowledge">travel technology lexicon</a>.',
    ],
    faqs: [
      {
        question: 'What is an OBT in travel?',
        answer:
          'An OBT, or online booking tool, is the self-service software employees use to book business travel within their organisation’s policy, usually alongside a travel management company.',
      },
      {
        question: 'What is the difference between an OBT and a TMC?',
        answer:
          'The OBT is the software used to search and book. The TMC is the agency that tickets bookings, manages changes and provides support. Most corporate programmes use both.',
      },
      {
        question: 'What is a good OBT adoption rate?',
        answer:
          'It depends on the organisation and its travel patterns, but programmes generally aim for most routine bookings to be made online, keeping agent-assisted bookings for complex trips and changes.',
      },
      {
        question: 'Is an online booking tool the same as a self-booking tool?',
        answer:
          'Yes. Online booking tool (OBT), self-booking tool (SBT) and corporate booking tool are different names for the same type of software.',
      },
    ],
    keywords: [
      'online booking tool',
      'obt tool',
      'obt software',
      'what is obt in travel',
      'self booking tool',
      'corporate booking tool',
    ],
    nlpTerms: [
      'travel management company',
      'travel policy',
      'adoption rate',
      'leakage',
      'negotiated rates',
      'approval workflow',
    ],
    sources: [
      {
        title: 'Travel Agent Services (561510) Statement of Work, Refresh 31',
        publisher: 'U.S. General Services Administration',
        url: 'https://www.gsa.gov/system/files?file=REFRESH+31_Travel+Agent+Services+561510+SOW+-+V1.1+040926.pdf',
      },
      {
        title: 'Making Informed, Cost-Saving Travel Decisions and SmartPay Essentials (2024 training forum)',
        publisher: 'GSA SmartPay',
        url: 'https://smartpay.gsa.gov/files/smartpay-training-forum/2024/Making_Informed_Cost_Saving_Travel_Decisions_and_SmartPay_Essentials_v2.pdf',
      },
      {
        title: 'How Concur Travel Works',
        publisher: 'SAP Concur',
        url: 'https://www.concur.com/blog/article/how-concur-travel-works',
      },
      {
        title: 'SAP Concur Trip Approval',
        publisher: 'SAP Concur',
        url: 'https://www.concur.com/products/trip-approval',
      },
      CONCUR_NDC,
      IATA_NDC_FACT_SHEET,
    ],
  },
  {
    slug: 'virtual-credit-cards',
    title: 'Virtual Cards (VCC) for Business Travel',
    description:
      'How virtual credit cards work in business travel: single-use card numbers for hotel payments, their controls, reconciliation benefits and common issues.',
    category: CATEGORY,
    directAnswer:
      'A virtual credit card (VCC) is a card number issued digitally, without a physical card, and restricted by rules such as amount, validity dates and merchant type. In business travel, virtual cards are most often generated for a single booking — commonly a hotel stay — so the supplier is paid directly, the charge is tied to that reservation, and the number cannot be reused elsewhere.',
    content: [
      'Virtual cards are part of the commercial card programmes offered by banks and payment providers. Rather than relying on travellers’ own cards, an organisation or its travel management company generates a card number when it is needed, sets limits on how it can be used, and sends it to the supplier.',
      'In India, the Reserve Bank of India’s directions on credit and debit cards define both as physical or virtual payment instruments, so a virtual card issued by a bank is regulated as a card like any other.',
      '<h2>How does a virtual card payment work?</h2>',
      '<ul><li>A booking is made — for example, a three-night hotel stay.</li><li>A virtual card number is generated with a limit matching the booking value, a validity period covering the stay, and restrictions on the type of merchant that can charge it.</li><li>The card details are sent to the hotel with the reservation, usually with instructions on which charges are covered.</li><li>The hotel charges the card at check-in or check-out. Charges outside the rules are declined.</li><li>Depending on the card provider, the transaction can carry reference data such as the booking ID, traveller and cost centre, which makes reconciliation easier.</li></ul>',
      '<h2>What is the difference between single-use and multi-use cards?</h2>',
      'A single-use virtual card is valid for one transaction or one booking and then expires, which keeps exposure small if the number is compromised. Multi-use virtual cards stay valid for a set period or supplier and suit recurring payments. Travel programmes also use lodge cards: a card account held with a travel management company and charged centrally, typically for airline tickets. It works on a similar principle of central billing with no card in the traveller’s hands.',
      comparisonTable({
        id: 'corporate-lodge-virtual',
        caption: 'Corporate card, lodge card and virtual card compared',
        rowHeader: 'Compared on',
        columns: ['Corporate card', 'Lodge card', 'Virtual card'],
        rows: [
          {
            label: 'Form',
            cells: [
              'A physical card in the employee’s name',
              'A card account with no physical card, lodged with a travel agency',
              'A card number generated digitally when it is needed',
            ],
          },
          {
            label: 'Who uses it',
            cells: ['The employee', 'The travel agency, on the company’s behalf', 'The supplier it is sent to, such as a hotel'],
          },
          {
            label: 'Typical use',
            cells: [
              'Spending during the trip, such as meals, taxis, fuel and parking',
              'Bookings made through the travel agency, mainly air tickets',
              'One booking or supplier, often a hotel stay',
            ],
          },
          {
            label: 'Controls',
            cells: [
              'The card’s credit limit',
              'Usable only through the travel agency and its partners',
              'Amount, validity period, currency and merchant type; a single-use number expires after one charge',
            ],
          },
          {
            label: 'Who is billed',
            cells: [
              'The company, the employee or both, depending on the card',
              'The company, on one central statement',
              'The company, with each charge matching a booking',
            ],
          },
        ],
      }),
      '<h2>Why do organisations use virtual cards?</h2>',
      '<ul><li><strong>Less out-of-pocket spend.</strong> Travellers do not pay for the room themselves and claim it back later.</li><li><strong>Easier reconciliation.</strong> Each charge maps to one booking, so finance teams can often match spend without waiting for receipts.</li><li><strong>Spend control.</strong> Limits prevent charges above the approved amount or outside the travel dates.</li><li><strong>Lower fraud exposure.</strong> A number restricted to one booking has little value if it is intercepted.</li></ul>',
      '<h2>What goes wrong, and how can it be avoided?</h2>',
      'A common issue happens at the hotel front desk. If the property has not processed the virtual card details sent in advance, staff may ask the traveller for a personal card. Clear instructions in the booking, and hotels used to receiving virtual payments, reduce this. The second issue is scope: many programmes cover only the room rate and taxes, so meals, parking and other incidentals still need a separate payment. Travellers should know before arrival what the virtual card covers.',
      'Card limits can also cause declines when a stay is extended or a rate changes, so the process for adjusting or reissuing a card needs to be quick.',
      'Related payment and programme terms are defined in the <a href="/knowledge">travel technology lexicon</a>.',
    ],
    faqs: [
      {
        question: 'What is a VCC in travel?',
        answer:
          'A VCC, or virtual credit card, is a digitally issued card number with built-in limits, often created for a single hotel or supplier booking so the supplier is paid directly and the charge matches that reservation.',
      },
      {
        question: 'What is a temporary virtual card?',
        answer:
          'A temporary or single-use virtual card is valid for one transaction or a short period and then expires, which limits the risk if the number is exposed.',
      },
      {
        question: 'Do virtual cards cover hotel incidentals?',
        answer:
          'Only if the programme sets them up to. Many cover the room rate and taxes only, leaving meals, parking and other extras to be paid separately.',
      },
      {
        question: 'Why did the hotel ask for my card when a virtual card was provided?',
        answer:
          'Usually because the property had not processed the virtual card details before arrival, or because it wants a deposit for incidentals. Clear payment instructions on the booking help avoid this.',
      },
    ],
    keywords: [
      'virtual credit card',
      'vcc travel',
      'virtual card hotel payment',
      'temp vcc',
      'single use virtual card',
      'business travel payments',
    ],
    nlpTerms: [
      'commercial card programme',
      'lodge card',
      'reconciliation',
      'merchant category',
      'single-use card',
      'travel management company',
    ],
    sources: [
      {
        title: 'How virtual cards can simplify commercial payments: A primer',
        publisher: 'Mastercard',
        url: 'https://www.mastercard.com/us/en/news-and-trends/Insights/2024/virtual-cards-101-simplifying-commercial-payments.html',
      },
      {
        title: 'Glossary of Terms and Acronyms: single-use and multi-use virtual card numbers',
        publisher: 'Mastercard Developers',
        url: 'https://developer.mastercard.com/iccp/documentation/glossary/',
      },
      {
        title: 'Visa Virtual Card for Business',
        publisher: 'Visa',
        url: 'https://www.visa.com/en-us/business/cards/virtual-card',
      },
      {
        title: 'AirPlus Virtual Cards Classic',
        publisher: 'AirPlus',
        url: 'https://airplus.com/en-us/solutions/business-travel/virtual-cards/',
      },
      {
        title: 'Lodge card and corporate card: what’s the difference?',
        publisher: 'AirPlus',
        url: 'https://comms.airplus.com/en/blog/lodge-card-and-corporate-card-whats-the-difference',
      },
      {
        title: 'A step-by-step guide to using virtual cards for hotel stays',
        publisher: 'Conferma',
        url: 'https://www.conferma.com/resources/a-step-by-step-guide-to-using-virtual-cards-for-hotel-stays/',
      },
      {
        title: 'Master Direction – Credit Card and Debit Card – Issuance and Conduct Directions, 2022',
        publisher: 'Reserve Bank of India',
        url: 'https://www.rbi.org.in/Scripts/BS_ViewMasDirections.aspx?id=12300',
      },
    ],
  },
];
